import React from 'react'

import Checkbox from '../../Form/Checkbox'
import SortColumn from '../../SortColumn/SortColumn'
import UnsortedColumn from '../../UnsortedColumn/UnsortedColumn'
import type StandardTable from '../StandardTable'
import {
  type ConfigItemType,
  type TableSortingProps,
} from '../StandardTable.models'
import styles from './_mobile-table.module.scss'
import { type ActiveTableFilterProps } from '../TableFilter/TableFilter'
import { type SelectDisplayOption } from '../../Selects/SelectDisplay/SelectDisplay'
import { Button, Icon, Popover, SelectDisplay } from '../../../module'
import { c } from '../../../translations/LibraryTranslationService'

type MobileTableHeaderProps<DataItem> = {
  /** Ref to keep track of header position */
  headerPositionRef: React.Ref<HTMLDivElement>
  /** Props needed if the table has checkboxes. */
  checkboxProps?: {
    checkAll: boolean
    enableCheckAll: boolean
    handleCheck: ({
      checkboxValue,
      checkedItemData,
      name,
    }: {
      checkboxValue: boolean
      checkedItemData: DataItem & { name?: string }
      name?: string
    }) => void
    handleCheckAllOrNone: (checkAll: boolean) => void
    loading: boolean
    someChecked: boolean
  }
  /** Main column to display on left */
  mainColumn: ConfigItemType<unknown, Record<string, unknown>>
  /** Secondary column to display on right */
  activeColumn: ConfigItemType<unknown, Record<string, unknown>>
  /** If the table has data */
  hasData: boolean
  /** Name by which the active mobile column is stored */
  localStorageName: string
  /** Used for displaying active custom filters if any */
  activeFilters: React.ComponentProps<typeof StandardTable>['activeFilters']
  /** Active column selected */
  activeSecondaryColumn: string
  /** Used for displaying active custom filters if any */
  tableHeaderActiveFilters?: ActiveTableFilterProps
  checkboxSelectOptions?: {
    options?: SelectDisplayOption[]
    callout: (option: SelectDisplayOption) => void
    active?: string
  }
  /** This value allows the checkAll to be shown, but in a disabled state */
  disableCheckAll?: boolean
} & TableSortingProps

const MobileTableHeader = ({
  headerPositionRef,
  checkboxProps,
  mainColumn,
  activeColumn,
  hasData,
  sort,
  sortBy,
  localStorageName,
  activeFilters,
  activeSecondaryColumn,
  tableHeaderActiveFilters,
  checkboxSelectOptions,
  disableCheckAll,
}: MobileTableHeaderProps<any>): React.JSX.Element => {
  const enableCheckAll = checkboxProps?.enableCheckAll

  const [selectedOption, setSelectedOption] =
    React.useState<SelectDisplayOption>()

  return (
    <div ref={headerPositionRef} className={`${styles.row} ${styles.header}`}>
      <div
        className={styles.column}
        style={{ ...mainColumn?.style, minWidth: 'auto' }}
      >
        {checkboxProps && (
          <div className={styles.selectCheckBoxHeader}>
            <Checkbox
              name='checkbox'
              stateName={enableCheckAll ? 'allLoaded' : 'all'}
              checked={checkboxProps?.checkAll}
              partialChecked={checkboxProps?.someChecked}
              disabled={checkboxProps?.loading || disableCheckAll}
              callout={(name, value) =>
                checkboxProps?.handleCheck({
                  checkboxValue: value,
                  checkedItemData: { name: '' },
                  name,
                })
              }
              label=''
              hideLabel
            />
            {checkboxSelectOptions ? (
              <Popover
                noPadding
                position='bottom'
                popoverContent={({ setVisible }) => (
                  <SelectDisplay
                    options={
                      checkboxSelectOptions.options ?? [
                        { name: 'all', label: c('all') },
                        { name: 'none', label: c('none') },
                      ]
                    }
                    callout={(option) => {
                      setSelectedOption(option)
                      checkboxSelectOptions.callout(option)
                      setVisible(false)
                      if (option.name === 'all') {
                        checkboxProps?.handleCheckAllOrNone(true)
                      } else if (option.name === 'none') {
                        checkboxProps?.handleCheckAllOrNone(false)
                      }
                    }}
                    active={
                      checkboxSelectOptions?.active
                        ? (checkboxSelectOptions.active as keyof SelectDisplayOption)
                        : selectedOption
                          ? (selectedOption?.name === 'all' ||
                              selectedOption.name === 'none') &&
                            checkboxProps?.checkAll
                            ? ('all' as keyof SelectDisplayOption)
                            : (selectedOption?.name === 'none' ||
                                  selectedOption.name === 'all') &&
                                !checkboxProps?.checkAll
                              ? ('none' as keyof SelectDisplayOption)
                              : (selectedOption?.name as keyof SelectDisplayOption)
                          : checkboxProps?.checkAll
                            ? ('all' as keyof SelectDisplayOption)
                            : ('none' as keyof SelectDisplayOption)
                    }
                  />
                )}
              >
                {({ setVisible }) => (
                  <Button as='unstyled' onClick={() => setVisible(true)}>
                    <Icon icon='carat' iconSize='8px' />
                  </Button>
                )}
              </Popover>
            ) : null}
          </div>
        )}
        {!mainColumn?.noSort && hasData && sort && sortBy ? (
          <SortColumn
            label={mainColumn?.label}
            {...(mainColumn?.options
              ? { options: mainColumn?.options }
              : { propName: mainColumn?.name ?? '' })}
            sorter={sort}
            customClass={mainColumn?.className}
            active={sortBy}
            localStorageName={localStorageName}
            lowerCaseParam={mainColumn?.lowerCaseParam}
            tooltip={mainColumn?.tooltip}
            filter={mainColumn?.filter}
            activeFilters={activeFilters}
            beta={mainColumn?.beta}
            tableHeaderActiveFilters={tableHeaderActiveFilters}
          />
        ) : (
          <UnsortedColumn
            label={mainColumn?.label}
            tooltip={mainColumn?.tooltip}
            className={mainColumn?.className ?? ''}
            beta={mainColumn?.beta}
          />
        )}
      </div>
      {activeSecondaryColumn && (
        <div
          className={`${styles.column} ${styles.activeColumn}`}
          style={{ ...activeColumn?.style, minWidth: 'auto' }}
        >
          {!activeColumn?.noSort && hasData && sort && sortBy ? (
            <SortColumn
              label={activeColumn?.label}
              {...(activeColumn?.options
                ? { options: activeColumn?.options }
                : { propName: activeColumn?.name ?? '' })}
              sorter={sort}
              customClass={activeColumn?.className}
              active={sortBy}
              localStorageName={localStorageName}
              lowerCaseParam={activeColumn?.lowerCaseParam}
              tooltip={activeColumn?.tooltip}
              filter={activeColumn?.filter}
              activeFilters={activeFilters}
              beta={activeColumn?.beta}
              tableHeaderActiveFilters={tableHeaderActiveFilters}
            />
          ) : (
            <UnsortedColumn
              label={activeColumn?.label}
              tooltip={activeColumn?.tooltip}
              className={activeColumn?.className ?? ''}
              beta={activeColumn?.beta}
            />
          )}
        </div>
      )}
    </div>
  )
}

export default MobileTableHeader
